import { useEffect, useState, useMemo } from "react";
import { api, formatCurrency, formatPercent, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog to validate (check criteria) a seller commission for a completed order.
 * Props:
 *  - open, onOpenChange
 *  - order: { id, reference, client_name, amount_ht, paid_amount, rate, gross_commission }
 *  - onSaved: callback after successful save
 */
export const CommissionValidationDialog = ({ open, onOpenChange, orderId, onSaved }) => {
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.getOrderCommissionValidation(orderId);
        setData(res.data);
        const prev = res.data.validation?.checked_criteria_ids || [];
        // If no validation exists, default all criteria to checked (historique/e1)
        const initial = res.data.validation
          ? new Set(prev)
          : new Set((res.data.criteria || []).map((c) => c.id));
        setChecked(initial);
        setNotes(res.data.validation?.notes || "");
      } catch (e) {
        toast.error(getErrorMessage(e, "Erreur de chargement"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, orderId]);

  const criteria = useMemo(() => data?.criteria || [], [data]);
  const totalWeight = useMemo(
    () => criteria.reduce((s, c) => s + (c.weight || 0), 0) || 100,
    [criteria]
  );
  const checkedWeight = useMemo(
    () => criteria.filter((c) => checked.has(c.id)).reduce((s, c) => s + (c.weight || 0), 0),
    [criteria, checked]
  );
  const ratio = totalWeight > 0 ? checkedWeight / totalWeight : 1;

  const paid = data?.paid_amount || 0;
  const paidHt = data?.paid_amount_ht ?? paid;
  const vatRate = data?.order?.vat_rate ?? 20;
  const rate = data?.order?.seller_id
    ? (data?.criteria?.length ? undefined : undefined)
    : undefined;
  // We compute gross from rate provided via data.order (not yet present) — derive from the parent row instead
  // For precision, call endpoint that returns rate? We'll use a fallback from props in the dialog caller.
  const toggle = (id) => {
    const n = new Set(checked);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setChecked(n);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.saveOrderCommissionValidation(orderId, {
        checked_criteria_ids: Array.from(checked),
        notes: notes || null,
      });
      toast.success("Validation enregistrée");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="commission-validation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Valider la commission</DialogTitle>
          <DialogDescription>
            Cochez les critères satisfaits pour cette commande. La commission est proratisée selon le poids des critères cochés.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !data ? null : (
          <div className="space-y-5">
            <div className="p-3 rounded-sm border bg-muted/30 space-y-1">
              <p className="font-medium">{data.order.reference} — {data.order.client_name}</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>Montant HT commande : <strong>{formatCurrency(data.order.amount_ht)}</strong></div>
                <div>TVA : <strong>{vatRate}%</strong></div>
                <div>Payé TTC : <strong className="text-foreground">{formatCurrency(paid)}</strong></div>
                <div>Base commission (HT) : <strong className="text-foreground">{formatCurrency(paidHt)}</strong></div>
              </div>
            </div>

            {criteria.length === 0 ? (
              <div className="p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <p className="text-sm">
                  Aucun critère défini pour ce vendeur. Définissez-les depuis la page <strong>Vendeurs</strong> pour activer la validation.
                  En l'absence de critères, 100% de la commission est appliquée.
                </p>
              </div>
            ) : (
              <>
                {Math.abs(totalWeight - 100) > 0.01 && (
                  <div className="text-xs text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    La somme des poids des critères vaut {totalWeight}% (au lieu de 100%).
                    Le calcul utilise le ratio poids cochés / poids total.
                  </div>
                )}
                <div className="space-y-2">
                  {criteria.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 p-3 rounded-sm border cursor-pointer hover:bg-muted/50"
                      data-testid={`criterion-row-${c.id}`}
                    >
                      <Checkbox
                        checked={checked.has(c.id)}
                        onCheckedChange={() => toggle(c.id)}
                        data-testid={`criterion-check-${c.id}`}
                      />
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-sm">{c.label}</span>
                        <span className="text-xs text-muted-foreground">{c.weight}%</span>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="validation-notes">Notes (optionnel)</Label>
              <Textarea
                id="validation-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                data-testid="validation-notes"
              />
            </div>

            <div className="p-3 rounded-sm bg-primary/5 border border-primary/20 space-y-1">
              <div className="flex justify-between text-sm">
                <span>Poids des critères cochés</span>
                <strong>{checkedWeight}% / {totalWeight}%</strong>
              </div>
              <div className="flex justify-between text-sm">
                <span>Ratio appliqué</span>
                <strong>{formatPercent(ratio)}</strong>
              </div>
              <div className="flex justify-between text-sm">
                <span>Base de calcul (payé HT)</span>
                <strong>{formatCurrency(paidHt)}</strong>
              </div>
              {data.validation?.validated_at && (
                <p className="text-xs text-muted-foreground pt-1">
                  Dernière validation : {new Date(data.validation.validated_at).toLocaleString("fr-FR")}
                  {data.validation.validated_by ? ` par ${data.validation.validated_by}` : ""}
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="save-validation-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Enregistrer la validation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommissionValidationDialog;
